import { type EditAction } from '@/application';
import { type EntitySource, type Evaluated } from '@/domain/construction';
import { type Vec2, vec } from '@/domain/math';
import { createEntity, type EntityId } from '@/domain/model';

import { type SnapResult, snap, SnapKind } from '../Snapping';

import { type PointerInput, type ToolContext } from './Tool';

/**
 * Turning a click into a point.
 *
 * Shared by every tool that places a point, because the decision is subtle and getting it
 * wrong in one tool but not another is exactly the kind of inconsistency users notice
 * without being able to name.
 *
 * The snap result decides what *kind* of point gets created, and that choice is the whole
 * difference between a drawing program and a geometry editor:
 *
 * | Snapped to      | Result                                                            |
 * | --------------- | ----------------------------------------------------------------- |
 * | An existing point | No new entity at all — the existing one is reused.               |
 * | A curve         | A **locus point**: exactly on that curve forever (ADR-0003).       |
 * | An intersection | An **intersection point**: exact, and it follows both parents.     |
 * | Grid or nothing | A free point.                                                     |
 */

export interface PointPlacement {
  readonly id: EntityId;
  /** Empty when an existing point was reused. */
  readonly actions: readonly EditAction[];
  /** True when nothing new was created. */
  readonly reused: boolean;
}

/**
 * What the pointer is currently on, as far as placing a point is concerned.
 *
 * Here rather than in whichever tool happened to need it first, for the reason above: the
 * snap and the point it becomes are one decision, and a tool that snapped differently would
 * place points differently for no reason a user could name.
 */
export function currentSnap(context: ToolContext, input: PointerInput): SnapResult {
  // The primary modifier temporarily disables snapping — the standard escape hatch for
  // placing something deliberately *near* but not *on* existing geometry.
  if (input.modifiers.primary) {
    return { world: input.world, kind: SnapKind.None };
  }
  return snap(
    { scene: context.scene, zoom: context.zoom, settings: context.snapSettings },
    input.world
  );
}

/**
 * The point a thing is to *hang from*, when the user asked for the thing and not the point.
 *
 * ## The report
 *
 * "Việc dựng hình khối cứ phải có 1 điểm làm tâm, việc này có cần thiết không?" — must building
 * a solid always start with a point to be its centre. Placing a cube meant placing a point and
 * then placing a cube on it, and a click anywhere else did nothing at all: a tool that refuses
 * every click on an empty canvas is one a user reasonably concludes is broken.
 *
 * ## Why the point stays
 *
 * It is what makes a solid a construction rather than a picture. The solid follows it, so the
 * figure can be dragged, the solid cascades away with it, and the whole thing reloads from the
 * file as a thing that can still be moved. Taking the anchor out of the model would buy a
 * simpler first click and cost all of that; what was worth taking out is the *user* having to
 * make it, which is what this does.
 *
 * ## Why it is invisible, and marked
 *
 * The centre of a cube is not a point of the figure. A textbook draws no dot there, and one that
 * appeared uninvited would read as a mistake — the very thing the report was about, merely
 * automated. Invisible is also unpickable and unsnappable, since `hitTestAll` skips what is not
 * drawn, so it cannot be caught by accident later.
 *
 * `isConstruction` is what says it is scaffolding rather than a point somebody hid. Without the
 * mark the two are indistinguishable: both are invisible points, and a cleanup that guessed
 * between them would eventually delete a user's own work. With it, the anchor can be swept away
 * with the solid it holds up — see `unreachableParentsOf` — and a hidden point of the user's own
 * is left exactly where they put it.
 *
 * Only when the click meant nothing else. Landing on an existing point means *that* point, and
 * landing on a curve means a point on the curve — through `placePoint`, so a solid hangs off
 * geometry the same way every other construction does, and can be slid along it.
 */
export function placeAnchor(
  context: ToolContext,
  snapped: SnapResult,
  world: Vec2
): PointPlacement {
  if (snapped.kind !== SnapKind.None && snapped.kind !== SnapKind.Grid) {
    return placePoint(context, snapped, world);
  }

  const id = context.nextId('c');
  return {
    id,
    actions: [
      {
        kind: 'add-entity',
        entity: createEntity(id, { op: 'free-point', position: snapped.world }, context.style, {
          visible: false,
          isConstruction: true,
        }),
      },
    ],
    reused: false,
  };
}

export function placePoint(context: ToolContext, snap: SnapResult, world: Vec2): PointPlacement {
  // Reusing an existing point rather than stacking a duplicate on top is what keeps a
  // construction connected: a segment drawn to a vertex must depend on that vertex.
  if (snap.kind === SnapKind.Point && snap.entity !== undefined) {
    return { id: snap.entity, actions: [], reused: true };
  }

  if (snap.kind === SnapKind.SolidVertex) return onSolidVertex(context, snap, world);
  if (snap.kind === SnapKind.SolidEdge) return onSolidEdge(context, snap, world);

  const source = sourceFor(snap, world);
  const id = context.nextId(prefixFor(source));
  return {
    id,
    actions: [{ kind: 'add-entity', entity: createEntity(id, source, context.style) }],
    reused: false,
  };
}

/**
 * A point on an edge of a solid: a division of the two corners, computed in space.
 *
 * ## Why not a locus point on the drawn line
 *
 * Because the drawn line is a shadow. An edge of a pyramid is a line **in space**, and this
 * product's whole care is with what that costs: whether a segment through the point runs inside
 * the block and must be dashed, whether an angle at it is right in space rather than merely
 * right on the page, and where it ends up when the solid turns. A point stuck to the projection
 * knows none of that, and gets all three wrong *invisibly* — the figure is correct from the one
 * angle it was built at and quietly false from every other.
 *
 * `ratio-point` interpolates the corners' space positions along with their page positions, so
 * the new point carries an `in3d` of its own. It is the same reason `ratio-point` exists at all
 * rather than being a locus point on a drawn segment.
 *
 * ## Why it may create three entities from one click
 *
 * Because the division needs its two corners to *be* something. A solid figure written in the
 * script already names every corner, so usually they are found and only the point is new. A
 * corner that was never named, or was deleted, is added here — an entity for a corner that was
 * always in the figure, made addressable because something now refers to it.
 */
/**
 * A corner of a solid, made addressable.
 *
 * The corner was always in the figure — the solid has drawn it since it was placed — but a
 * *drawing* of a corner is not something a problem can name, hang a segment off, or measure to.
 * This is what turns one into an entity, and it is the whole of what "click the corner" should
 * do: no new geometry, no second point, just a name for a place the figure already had.
 *
 * Reused rather than stacked, exactly as an existing point is at the top of `placePoint`. A
 * teacher who clicks the same corner twice means *that* corner, and a second entity on the same
 * spot is the thing that makes a construction quietly wrong later — two names for one place, and
 * whichever the next click happens to find.
 *
 * A solid built from a drawn base, or written in the script, arrives with every corner already
 * named; there the loop below finds one and creates nothing. A solid dropped from the toolbar
 * arrives with none, and this is where its first named corner comes from.
 */
function onSolidVertex(context: ToolContext, snap: SnapResult, world: Vec2): PointPlacement {
  const { host, corner } = snap;
  if (host === undefined || corner === undefined) return placeFree(context, world);

  for (const entity of context.document.entities.values()) {
    const { source } = entity;
    if (source.op === 'solid-vertex' && source.solid === host && source.index === corner) {
      return { id: entity.id, actions: [], reused: true };
    }
  }

  const id = context.nextId('sv');
  return {
    id,
    actions: [
      {
        kind: 'add-entity',
        entity: createEntity(id, { op: 'solid-vertex', solid: host, index: corner }, context.style),
      },
    ],
    reused: false,
  };
}

function onSolidEdge(context: ToolContext, snap: SnapResult, world: Vec2): PointPlacement {
  const { host, corners, parameter } = snap;
  if (host === undefined || corners === undefined || parameter === undefined) {
    return placeFree(context, world);
  }

  const actions: EditAction[] = [];

  /** The entity standing for corner `index` of this solid, created if there is not one. */
  const cornerId = (index: number): EntityId => {
    for (const entity of context.document.entities.values()) {
      const { source } = entity;
      if (source.op === 'solid-vertex' && source.solid === host && source.index === index) {
        return entity.id;
      }
    }

    const id = context.nextId('sv');
    actions.push({
      kind: 'add-entity',
      entity: createEntity(id, { op: 'solid-vertex', solid: host, index }, context.style),
    });
    return id;
  };

  const a = cornerId(corners[0]);
  const b = cornerId(corners[1]);

  const id = context.nextId('r');
  actions.push({
    kind: 'add-entity',
    entity: createEntity(id, { op: 'ratio-point', a, b, t: parameter }, context.style),
  });

  return { id, actions, reused: false };
}

function placeFree(context: ToolContext, world: Vec2): PointPlacement {
  const id = context.nextId('p');
  return {
    id,
    actions: [
      {
        kind: 'add-entity',
        entity: createEntity(id, { op: 'free-point', position: world }, context.style),
      },
    ],
    reused: false,
  };
}

function sourceFor(snap: SnapResult, world: Vec2): EntitySource {
  if (
    snap.kind === SnapKind.Intersection &&
    snap.crossing !== undefined &&
    snap.crossingIndex !== undefined
  ) {
    return {
      op: 'intersection',
      a: snap.crossing[0],
      b: snap.crossing[1],
      index: snap.crossingIndex,
    };
  }

  if (snap.kind === SnapKind.Curve && snap.host !== undefined && snap.parameter !== undefined) {
    // `edge` names a side when the host is a polygon, and is simply absent otherwise — so a
    // point on a triangle's side is the same kind of thing as a point on a drawn segment.
    return {
      op: 'locus-point',
      host: snap.host,
      t: snap.parameter,
      ...(snap.edge === undefined ? {} : { edge: snap.edge }),
    };
  }

  return { op: 'free-point', position: snap.kind === SnapKind.None ? world : snap.world };
}

/**
 * Id prefix by construction kind, so a saved file reads as what it is.
 *
 * Written as conditionals rather than a switch: only three of the thirty-five operations
 * can ever reach here, and an exhaustive switch would list the other thirty-two for no
 * benefit.
 */
function prefixFor(source: EntitySource): string {
  if (source.op === 'intersection') return 'x';
  if (source.op === 'locus-point') return 'lp';
  return 'p';
}

/**
 * Where a page position lands in a free plane's own axes, clamped to the drawn parallelogram.
 *
 * ## Running the projection backwards
 *
 * The plane is drawn as four corners written `+u+v`, `−u+v`, `−u−v`, `+u−v`, so half of
 * `corner0 − corner1` is the page image of `u` scaled by the half-width, and the same for `v`
 * against `corner3`. Those two vectors span the parallelogram, and a frame is **affine**, so the
 * pointer's offset from the centre is one linear combination of them — a 2×2 solve, exact.
 *
 * Reading the axes off the drawn shape rather than recomputing them from the tilt is deliberate:
 * there is then one description of where the plane is, and it is the one the user is looking at.
 *
 * `null` when the two page axes are parallel, which is a plane seen exactly edge-on. There is no
 * position to compute there — the whole plane is a line on the page — and refusing the drag is the
 * honest answer.
 */
export function inPlaneAt(
  plane: Evaluated | null | undefined,
  at: Vec2
): { readonly u: number; readonly v: number } | null {
  if (plane?.kind !== 'polygon' || plane.frame === undefined) return null;

  const [a, b, , d] = plane.vertices;
  if (plane.vertices.length !== 4 || a === undefined || b === undefined || d === undefined) {
    return null;
  }

  const alongU = vec.scale(vec.subtract(a, b), 0.5);
  const alongV = vec.scale(vec.subtract(a, d), 0.5);

  // Half the width in world units, recovered from the corners in space — the page images above
  // are foreshortened and cannot say how far the drag went in the plane itself.
  const [spaceA, spaceB] = plane.in3d ?? [];
  if (spaceA === undefined || spaceB === undefined) return null;
  const half = Math.hypot(spaceA.x - spaceB.x, spaceA.y - spaceB.y, spaceA.z - spaceB.z) / 2;

  const centre = vec.scale(
    plane.vertices.reduce((sum, corner) => vec.add(sum, corner), vec.ZERO),
    1 / plane.vertices.length
  );
  const offset = vec.subtract(at, centre);

  // Cramer's rule on [alongU alongV] · (s, t) = offset.
  const determinant = alongU.x * alongV.y - alongU.y * alongV.x;
  if (Math.abs(determinant) < EDGE_ON) return null;

  const s = (offset.x * alongV.y - offset.y * alongV.x) / determinant;
  const t = (alongU.x * offset.y - alongU.y * offset.x) / determinant;

  const inside = (value: number) => Math.max(-1, Math.min(1, value));
  return { u: inside(s) * half, v: inside(t) * half };
}

/**
 * Below this the plane's two page axes are the same direction and it is drawn as a line.
 *
 * A determinant rather than an angle, because it is what the solve needs anyway, and scaled by
 * nothing on purpose: the axes are already page vectors, so this is an area in drawing units and
 * a plane that thin has no interior for a point to be dragged inside.
 */
const EDGE_ON = 1e-6;
